import { useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api, ApiError, type GenerationResponse } from "@/lib/api";

const before1 =
  "https://readdy.ai/api/search-image?query=A%20rough%20unfinished%20hand%20drawn%20pencil%20sketch%20of%20a%20modern%20perfume%20bottle%20on%20plain%20white%20paper%2C%20loose%20sketchy%20charcoal%20lines%2C%20incomplete%20outline%20doodle%2C%20minimal%20line%20art%2C%20empty%20white%20background%2C%20simple%20amateur%20drawing%2C%20slightly%20messy&width=800&height=600&seq=before-1&orientation=landscape";

const after1 =
  "https://readdy.ai/api/search-image?query=A%20stunning%20photorealistic%20luxury%20perfume%20bottle%20product%20photograph%2C%20crystal%20clear%20glass%20with%20iridescent%20teal%20and%20holographic%20reflections%2C%20floating%20on%20a%20soft%20white%20gradient%20background%2C%20professional%20studio%20lighting%20with%20soft%20shadows%2C%20premium%20commercial%20advertising%20shot%2C%20extremely%20high%20detail&width=800&height=600&seq=after-1&orientation=landscape";

const before2 =
  "https://readdy.ai/api/search-image?query=A%20rough%20unfinished%20pencil%20sketch%20of%20a%20human%20face%20portrait%20on%20plain%20white%20paper%2C%20loose%20sketchy%20lines%2C%20incomplete%20outline%2C%20minimal%20line%20art%2C%20empty%20white%20background%2C%20simple%20amateur%20doodle%20drawing%2C%20slightly%20messy%20proportions&width=800&height=600&seq=before-2&orientation=landscape";

const after2 =
  "https://readdy.ai/api/search-image?query=A%20stunning%20photorealistic%20professional%20studio%20portrait%20of%20a%20confident%20young%20woman%20with%20flawless%20skin%2C%20cinematic%20teal%20and%20cyan%20rim%20lighting%2C%20soft%20white%20gradient%20background%2C%20premium%20editorial%20beauty%20photography%2C%20extremely%20high%20detail%2C%20natural%20makeup&width=800&height=600&seq=after-2&orientation=landscape";

const videoPreview =
  "https://readdy.ai/api/search-image?query=A%20cinematic%20futuristic%20product%20animation%20still%20frame%20of%20a%20floating%20holographic%20medical%20device%20rotating%20in%20space%2C%20glowing%20teal%20and%20cyan%20neon%20lighting%2C%20translucent%20glassmorphism%20interface%20panels%20overlaid%2C%20depth%20of%20field%2C%20premium%20technology%20commercial%20aesthetic%2C%20high%20resolution&width=800&height=600&seq=video-preview&orientation=landscape";

function BeforeAfterSlider({ before, after, alt }: { before: string; after: string; alt: string }) {
  const { t } = useTranslation();
  const [pos, setPos] = useState(50);
  const ref = useRef<HTMLDivElement>(null);

  const handleMove = (clientX: number) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, pct)));
  };

  return (
    <div
      ref={ref}
      className="relative w-full aspect-[4/3] overflow-hidden rounded-2xl select-none cursor-ew-resize"
      onPointerDown={(e) => {
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        handleMove(e.clientX);
      }}
      onPointerMove={(e) => {
        if (e.buttons === 1) handleMove(e.clientX);
      }}
    >
      <img src={after} alt={t("home.demo.altGenerated", { subject: alt })} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
      <img
        src={before}
        alt={t("home.demo.altSketch", { subject: alt })}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />

      <div className="absolute top-0 bottom-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -translate-x-1/2 w-0.5 bg-background-50/90"></div>
        <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-background-50 text-foreground-900 shadow-md">
          <i className="ri-arrow-left-right-line text-sm"></i>
        </div>
      </div>

      <span className="absolute top-3 left-3 rounded-full bg-foreground-950/70 text-background-50 text-xs font-medium px-3 py-1">
        {t("home.demo.sketch")}
      </span>
      <span className="absolute top-3 right-3 rounded-full bg-gradient-to-r from-primary-500 to-accent-500 text-background-50 text-xs font-medium px-3 py-1">
        {t("home.demo.aiGenerated")}
      </span>
    </div>
  );
}

export default function DemoPreview() {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState("");
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [generatedExt, setGeneratedExt] = useState("png");
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  const handleGenerate = async () => {
    const trimmed = prompt.trim();
    if (!trimmed || generating) return;

    setGenerating(true);
    setGenError(null);
    setGeneratedImage(null);

    try {
      const data = await api.post<GenerationResponse>("/generate/image", { prompt: trimmed });

      if (data?.imageUrl) {
        setGeneratedImage(data.imageUrl);
        const mime = (data.mimeType || "image/png").split("/")[1] || "png";
        setGeneratedExt(mime);
      } else {
        throw new Error(t("home.demo.noImage"));
      }
    } catch (err) {
      setGenError(
        err instanceof ApiError && err.status === 422
          ? t("generate.errors.blocked")
          : err instanceof Error
            ? err.message
            : t("home.demo.genericError")
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <section id="demo" className="relative py-20 md:py-28 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
            {t("home.demo.eyebrow")}
          </span>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold text-foreground-950">
            {t("home.demo.title")}
          </h2>
          <p className="mt-4 text-foreground-600">
            {t("home.demo.subtitle")}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
          <BeforeAfterSlider before={before1} after={after1} alt={t("home.demo.perfumeBottle")} />
          <BeforeAfterSlider before={before2} after={after2} alt={t("home.demo.portrait")} />
        </div>

        {/* Video preview */}
        <div className="mt-8 relative overflow-hidden rounded-2xl group">
          <img src={videoPreview} alt={t("home.demo.videoAlt")} loading="lazy" decoding="async" className="w-full h-[320px] md:h-[420px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
          <button
            type="button"
            aria-label={t("home.demo.playVideo")}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-background-50/90 text-primary-600 transition-transform hover:scale-110 cursor-pointer"
          >
            <i className="ri-play-fill text-3xl md:text-4xl"></i>
          </button>
          <div className="absolute bottom-5 left-6">
            <span className="rounded-full bg-gradient-to-r from-primary-500 to-accent-500 text-background-50 text-xs font-medium px-3 py-1">
              {t("home.demo.videoBadge")}
            </span>
          </div>
        </div>

        {/* Generate demo */}
        <div className="mt-10 rounded-2xl border-2 border-dashed border-primary-300 bg-primary-50/40 p-8 md:p-10">
          <div className="max-w-xl mx-auto text-center">
            <i className="ri-magic-line text-4xl text-primary-600"></i>
            <h3 className="mt-4 text-lg font-bold text-foreground-950">{t("home.demo.generateTitle")}</h3>
            <p className="mt-2 text-sm text-foreground-600">
              {t("home.demo.generateSubtitle")}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-foreground-500">
              <Trans
                i18nKey="common.contentRules"
                components={{ terms: <Link to="/terms-of-use" className="text-primary-600 hover:underline" /> }}
              />
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !generating) void handleGenerate();
                }}
                placeholder={t("home.demo.promptPlaceholder")}
                className="flex-1 rounded-full border border-background-300/60 bg-background-50 px-5 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <button
                type="button"
                onClick={() => void handleGenerate()}
                disabled={generating || !prompt.trim()}
                className="whitespace-nowrap rounded-full bg-gradient-to-r from-primary-500 to-accent-500 px-7 py-3 text-sm font-semibold text-background-50 transition-transform hover:scale-[1.03] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
              >
                {generating ? t("home.demo.generating") : t("home.demo.generate")}
              </button>
            </div>

            {genError && (
              <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600 text-left">
                {genError}
              </div>
            )}

            {generating && (
              <div className="mt-6 flex items-center justify-center gap-3 text-primary-600 text-sm">
                <i className="ri-loader-4-line animate-spin text-xl"></i>
                <span>{t("home.demo.rendering")}</span>
              </div>
            )}

            {generatedImage && !generating && (
              <div className="mt-6">
                <img
                  src={generatedImage}
                  alt={t("home.demo.resultAlt")}
                  className="w-full max-w-lg mx-auto rounded-xl border border-background-200/70 object-cover"
                />
                <a
                  href={generatedImage}
                  download={`generated-image.${generatedExt}`}
                  className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-foreground-950 text-background-50 px-5 py-2.5 text-sm font-medium hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  <i className="ri-download-line"></i>
                  {t("common.actions.download")}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}